import { useRef, useState } from "react";
import { RefinementListItem } from "./RefinementListItem";
import React, { memo } from "react";
import { Box } from "@shared-components/Box/Box";
import { Button } from "theme-ui";
import { useInfiniteHits } from "react-instantsearch-hooks-web";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";

export type AlgoliaFacet =
  | "brand"
  | "abv"
  | "brewery"
  | "country"
  | "countryDk"
  | "sizegroup"
  | "themes"
  | "themesDk"
  | "year";

export type RefinementListProps = {
  label: string;
  attribute: AlgoliaFacet;
  attributeDk?: AlgoliaFacet;
};

export type RefinementListType = Array<RefinementListProps>;

type FilterViewProps = {
  activeLetter: string | undefined;
  handleActiveLetter: (letter?: string) => void;
};

const refinementLists: RefinementListType = [
  { label: "Brand", attribute: "brand" },
  { label: "Brewery", attribute: "brewery" },
  { label: "Country", attribute: "country", attributeDk: "countryDk" },
  { label: "Themes", attribute: "themes", attributeDk: "themesDk" },
  { label: "Alcohol", attribute: "abv" },
  { label: "Size", attribute: "sizegroup" },
  { label: "Year", attribute: "year" },
];

export const FilterView: React.FC<FilterViewProps> = memo(
  ({ handleActiveLetter, activeLetter }) => {
    const { get } = useDictionaryContext();
    const { currentCulture } = useSiteContext();
    const [currentItems, setCurrentItems] = useState();
    const languageEn = currentCulture === "en-us";

    const refinementListViewRef = useRef<HTMLDivElement | null>(null);

    const [activeFilter, setActiveFilter] = useState<string>(
      refinementLists[0].label
    );

    const [showLetters, setShowLetters] = useState(true);

    const alpha = Array.from(Array(26)).map((_e, i) => i + 65);
    const alphabet = alpha.map((x) => String.fromCharCode(x));
    alphabet.push(String.fromCharCode(198)); // Æ
    alphabet.push(String.fromCharCode(216)); // Ø
    alphabet.push(String.fromCharCode(197)); // Å

    const handleActiveFilter = (label: string) => {
      handleActiveLetter(undefined);
      setActiveFilter(label);

      if (label === "Alcohol" || label === "Size" || label === "Year") {
        setShowLetters(false);
      } else if (!showLetters) {
        setShowLetters(true);
      }
    };

    const { results } = useInfiniteHits();
    const { _state } = results!;
    const { disjunctiveFacetsRefinements } = _state;
    const hideCountRefinements = ["abv", "sizegroup", "year"];

    const handleCurrentItems = (items: any) => {
      setCurrentItems(items);
    };

    return (
      <div
        style={{
          height: "100%",
          overflow: "hidden",
          paddingRight: 10,
          paddingBottom: 11,
        }}
        ref={refinementListViewRef}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {refinementLists.map(({ label, attribute }, key) => {
            const dictionaryLabel = get(
              DICTIONARY_MAP.kiosk[`kiosk_filters_${attribute}`],
              currentCulture
            );

            const showCount = !hideCountRefinements.includes(attribute);
            return (
              <Button
                key={key}
                sx={{
                  fontFamily: "NyCarlsbergDisplay-Bold",
                  border: "4px solid",
                  borderColor: "mediumGray",
                  bg: activeFilter === label ? "mediumGray" : "transparent",
                  color: activeFilter === label ? "white" : "mediumGray",
                  fontSize: "smText",
                  px: "6",
                  py: "4",
                  mb: "1",
                  cursor: "pointer",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  mr: 1,
                  ":last-child": {
                    mr: 0,
                  },
                  ":focus": {
                    boxShadow: "none",
                  },
                }}
                onClick={() => handleActiveFilter(label)}
              >
                <span className="block">{dictionaryLabel}</span>{" "}
                {disjunctiveFacetsRefinements[attribute]?.length > 0 &&
                  showCount &&
                  `(${disjunctiveFacetsRefinements[attribute]?.length})`}
              </Button>
            );
          })}
        </Box>

        {showLetters && (
          <Box sx={{ display: "flex", flexWrap: "wrap", pt: 10, pb: 8 }}>
            {alphabet.map((letter, key) => {
              const resultsStartingWithLetter = currentItems
                ? (currentItems as any[]).some(({ value }) =>
                    value.startsWith(letter)
                  )
                : false;

              return (
                <Button
                  disabled={!resultsStartingWithLetter}
                  key={key}
                  onClick={() => handleActiveLetter(letter)}
                  sx={{
                    fontFamily:
                      activeLetter === letter
                        ? "NyCarlsbergDisplay-Bold"
                        : "NyCarlsbergDisplay-Regular",
                    height: "3rem",
                    width: "3rem",
                    color: "text",
                    fontSize: "h6",
                    bg: "transparent",
                    border: "none",
                    ":hover:not(:disabled)": {
                      bg: "gray",
                    },
                    ":focus": {
                      boxShadow: "none",
                    },
                    ":disabled": {
                      opacity: 0.4,
                      cursor: "default",
                    },
                  }}
                >
                  {letter}
                </Button>
              );
            })}
          </Box>
        )}
        {refinementLists.map(({ label, attribute, attributeDk }, key) => {
          return (
            <Box
              key={key}
              sx={{
                height: "full",
                display: activeFilter === label ? "block" : "none",
              }}
            >
              <RefinementListItem
                activeLetter={activeLetter}
                attribute={!languageEn && attributeDk ? attributeDk : attribute}
                activeFilter={activeFilter}
                label={label}
                handleCurrentItems={handleCurrentItems}
                texts={{
                  alcohol: get(
                    DICTIONARY_MAP.kiosk[`kiosk_filters_${attribute}`],
                    currentCulture
                  ) as string,
                }}
              />
            </Box>
          );
        })}
      </div>
    );
  }
);
